/* import __COLOCATED_TEMPLATE__ from './guideline-header.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Guideline from 'embercom/models/ai-agent/guidelines';
import { type PulseAccordionSection } from 'glint/pulse';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import {
  VOICE_UNSUPPORTED_CATEGORIES,
  CHAT_UNSUPPORTED_CATEGORIES,
} from 'embercom/components/ai-agent/guidance/channel-group-selector';

enum Stat {
  USED = 'used',
  RESOLVED = 'resolved',
  ROUTED = 'routed',
}

interface Signature {
  Args: {
    guideline: Guideline;
    section: PulseAccordionSection;
    header: PulseAccordionSection['Header'];
    index: number;
    category: string;
    chartDefinitionsHash: Record<string, Record<Stat, any>>;
    isNewGuideline?: boolean;
    categoryStampTitle?: string;
  };
  Element: HTMLDivElement;
}

export default class GuidelineHeader extends Component<Signature> {
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare permissionsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: $TSFixMe;

  @tracked selectedGuideline?: Guideline;
  @tracked selectedStat?: Stat;

  stats = [Stat.USED, Stat.RESOLVED, Stat.ROUTED];

  drillInColumns = [
    'conversation.id',
    'conversation.first_user_conversation_part_created_at',
    'conversation.fin.resolution_state',
    'conversation.channel_type',
    'conversation.bot_conversation_rating.rating_index',
    'conversation.fin.guidelines_applied.guideline_ids',
  ];

  get canAdminAccessReporting(): boolean {
    return this.permissionsService.currentAdminCan('can_access_reporting');
  }

  get attributeInfoResolver(): AttributeInfoResolver {
    return this.aiAgentGuidelineService.attributeInfoResolver();
  }

  get showReporting() {
    if (!this.canAdminAccessReporting) {
      return false;
    }
    let excludedCategories = ['copilot', 'voice_clarification', 'voice_tone'];

    if (excludedCategories.includes(this.args.category)) {
      return false;
    }

    return true;
  }

  get combinedAudienceChannelLabel(): string {
    let audienceLabel = this.audienceLabel;
    let channelsLabel = this.channelsLabel;
    return this.intl.t('ai-agent.guidance.channel-groups.audience-channel-combined', {
      audience: audienceLabel,
      channels: channelsLabel,
    });
  }

  private get audienceLabel(): string {
    let segmentIds = this.args.guideline.aiContentSegmentIds ?? [];
    if (segmentIds.length === 0) {
      return this.intl.t('ai-agent.guidance.channel-groups.everyone');
    }
    if (segmentIds.length === 1) {
      let segment = this.aiContentSegmentsService.segments.find(
        (s) => Number(s.id) === segmentIds[0],
      );
      return segment?.nameWithFallback ?? this.intl.t('ai-agent.guidance.channel-groups.everyone');
    }
    return this.intl.t('ai-agent.guidance.channel-groups.audiences-count', {
      count: segmentIds.length,
    });
  }

  private get channelsLabel(): string {
    let category = this.args.category as any;
    let isChatDisabled = CHAT_UNSUPPORTED_CATEGORIES.includes(category);
    let isVoiceDisabled = VOICE_UNSUPPORTED_CATEGORIES.includes(category);

    let targetChannels = this.args.guideline.targetChannels ?? [];

    // Determine the base channel list: explicit selection or expand "all channels"
    let base = targetChannels.length > 0 ? targetChannels : ['chat', 'email', 'voice'];

    // Filter out channels that are disabled for this category
    let effective = base.filter((ch: string) => {
      if (ch === 'voice' && isVoiceDisabled) {
        return false;
      }
      if (ch === 'chat' && isChatDisabled) {
        return false;
      }
      return true;
    });

    if (effective.length === 0 || effective.length === 3) {
      return this.intl.t('ai-agent.guidance.channel-groups.all-channels');
    }
    return effective
      .map((ch: string) => {
        if (ch === 'chat') {
          return this.intl.t('ai-agent.guidance.channel-groups.chat');
        }
        if (ch === 'email') {
          return this.intl.t('ai-agent.guidance.channel-groups.email');
        }
        if (ch === 'voice') {
          return this.intl.t('ai-agent.guidance.channel-groups.voice');
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
  }

  @action
  openSideDrawer(guideline: Guideline, stat: Stat, event: Event) {
    this.trackEvent(stat);
    event.stopPropagation();
    event.preventDefault();
    if (this.permissionsService.currentAdminCan('can_reporting__drillin__access')) {
      this.selectedGuideline = guideline;
      this.selectedStat = stat;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  closeSideDrawer() {
    this.selectedGuideline = undefined;
    this.selectedStat = undefined;
  }

  @action
  trackEvent(stat_clicked: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'guidance_stat',
      place: 'guidance_card',
      section: 'guidance',
      stat: stat_clicked,
    });
  }

  @action
  async cancelGuideline(guideline: Guideline) {
    guideline.rollbackAttributes();

    // If it's a saved guideline that is now empty (was just created or rolled back to empty),
    // delete it from the backend
    if (!guideline.isNew && !guideline.hasPrompt) {
      try {
        await guideline.destroyRecord();
      } catch (error) {
        console.error('Failed to delete empty guideline:', error);
      } finally {
        this.aiAgentGuidelineService.updatePreview();
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::GuidelineHeader': typeof GuidelineHeader;
    'ai-agent/guidance/guideline-header': typeof GuidelineHeader;
  }
}
